import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  AlertDialogService,
  Destroyable,
  isSavingRental,
  isSomething,
  Rental,
  SaveForm,
  selectAvailableRentalChildren,
  selectAvailableRentalParents,
  selectRentalChildren,
  setRentalChildren,
  setRentalChildrenComplete,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { startWith, switchMap } from 'rxjs/operators'
import * as lodash from 'lodash'
import { Observable, of } from 'rxjs'
import { RentalGroupSuccessDialogService } from '../rental-group-success-dialog/rental-group-success-dialog.service'
import { Actions, ofType } from '@ngrx/effects'

@Component({
  selector: 'app-parent-child-rental-dialog',
  templateUrl: './parent-child-rental-dialog.component.html',
  styleUrls: ['./parent-child-rental-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class ParentChildRentalDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    parent_id: ['', [Validators.required]],
    children: [[]],
  })

  get parentIdCtrl() {
    return this.form.get('parent_id') as FormControl
  }

  availableParents$ = this.store.pipe(select(selectAvailableRentalParents))
  availableChildren$: Observable<Rental[]>

  children: Rental[] = []

  loading$ = this.store.pipe(select(isSavingRental))

  constructor(
    public dialogRef: MatDialogRef<ParentChildRentalDialogComponent>,
    private fb: FormBuilder,
    private actions: Actions,
    private store: Store<any>,
    private successDialog: RentalGroupSuccessDialogService,
    private alert: AlertDialogService,
    @Inject(MAT_DIALOG_DATA) public data: { rentalId?: string }
  ) {
    super()
    if (this.data.rentalId) {
      this.form.patchValue({ parent_id: this.data.rentalId })
    }
  }

  ngOnInit(): void {
    const parentId$ = this.parentIdCtrl.valueChanges.pipe(startWith(this.parentIdCtrl.value))
    parentId$
      .pipe(
        switchMap((id) => this.store.pipe(select(selectRentalChildren(id)))),
        untilDestroy(this)
      )
      .subscribe((children) => {
        this.children = children
        this.form.patchValue({ children: children?.map((r) => r.id) })
      })

    this.availableChildren$ = parentId$.pipe(
      switchMap((id) => this.store.pipe(select(selectAvailableRentalChildren(id))))
    )

    this.actions
      .pipe(
        ofType(setRentalChildrenComplete),
        switchMap(({ updates }) => {
          if (updates.some((t) => t.id === this.data.rentalId && !t.changes.parent_id)) {
            this.showUnlinkedSuccessDialog()
            return of(null)
          }
          return this.showSuccessDialog()
        }),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.close()
      })
  }

  close() {
    this.dialogRef.close()
  }

  showUnlinkedSuccessDialog() {
    return this.alert.open({
      title: $localize`Parent-child setup unlinked!`,
      body: $localize`The parent-child relationship has been successfully removed from selected rentals.`,
    })
  }

  showSuccessDialog() {
    return this.successDialog
      .open({
        title: $localize`Parent-Child Setup Complete!`,
        message: $localize`Your rentals have been successfully combined into a Parent-Child setup.`,
        image: '/assets/images/rental-group/multi-unit-done-3.svg',
        steps: [
          $localize`The parent rental now organizes and manages child rentals under one listing.`,
          $localize`Each child rental retains its pricing, availability, and settings independently while being grouped under the parent rental.`,
          $localize`You can manage each rental separately while keeping them linked under the Parent-Child structure.`,
        ],
        buttons: [
          {
            label: $localize`Ok, got it`,
            icon: 'cs-icon cs-icon-check text-xs',
            classes: 'btn btn-secondary-info',
          },
        ],
      })
      .afterClosed()
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { children: newChildrenIds, parent_id } = this.form.getRawValue()
    const oldChildrenIds = this.children.map((t) => t.id)

    if (
      !lodash.difference(oldChildrenIds, newChildrenIds).length &&
      !lodash.difference(newChildrenIds, oldChildrenIds).length
    ) {
      this.store.dispatch(setRentalChildrenComplete({ updates: [] }))
      this.close()
      return
    }

    const addedChildren = lodash.difference(newChildrenIds, oldChildrenIds)
    const removedChildren = lodash.difference(oldChildrenIds, newChildrenIds)

    // set parent its self
    if (!isSomething(oldChildrenIds) && isSomething(addedChildren)) {
      addedChildren.push(parent_id)
    }

    // clear parent its self
    if (isSomething(oldChildrenIds) && !isSomething(newChildrenIds)) {
      removedChildren.push(parent_id)
    }

    this.store.dispatch(
      setRentalChildren({
        rentalId: parent_id,
        addedChildren,
        removedChildren,
      })
    )
  }
}
